/**
 * useImageViewerTransform —— 图片灯箱的变换状态机（F25）。
 *
 * 只持有 scale / rotation / translate 三组状态与对应的纯操作，不含任何
 * DOM 或拖拽事件细节（拖拽由组件层把位移增量喂给 `panBy`）。钳制与
 * 90° 循环规则集中在此，保证组件与单测共享同一份边界定义。
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue';

export const MIN_SCALE = 0.25;
export const MAX_SCALE = 5;
export const SCALE_STEP = 1.25;
const ROTATION_STEP = 90;
const FULL_TURN = 360;

export interface ImageViewerTransform {
  readonly scale: Ref<number>;
  /** 顺时针累计角度，规范化为 0/90/180/270。 */
  readonly rotation: Ref<number>;
  readonly translateX: Ref<number>;
  readonly translateY: Ref<number>;
  /** 缩放百分比文案（如 "125%"），供操作栏展示。 */
  readonly scaleLabel: ComputedRef<string>;
  /** 组合后的 CSS transform 串。 */
  readonly transformStyle: ComputedRef<string>;
  zoomIn(): void;
  zoomOut(): void;
  rotateLeft(): void;
  rotateRight(): void;
  /** 拖拽平移：累加位移增量（像素）。 */
  panBy(dx: number, dy: number): void;
  /** 恢复初始状态：scale=1、rotation=0、位移归零。 */
  reset(): void;
}

function clampScale(value: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
}

function normalizeRotation(deg: number): number {
  return ((deg % FULL_TURN) + FULL_TURN) % FULL_TURN;
}

export function useImageViewerTransform(): ImageViewerTransform {
  const scale = ref(1);
  const rotation = ref(0);
  const translateX = ref(0);
  const translateY = ref(0);

  const scaleLabel = computed(() => `${Math.round(scale.value * 100)}%`);
  const transformStyle = computed(
    () =>
      `translate(${translateX.value}px, ${translateY.value}px)` +
      ` scale(${scale.value}) rotate(${rotation.value}deg)`,
  );

  function zoomIn(): void {
    scale.value = clampScale(scale.value * SCALE_STEP);
  }

  function zoomOut(): void {
    scale.value = clampScale(scale.value / SCALE_STEP);
  }

  function rotateLeft(): void {
    rotation.value = normalizeRotation(rotation.value - ROTATION_STEP);
  }

  function rotateRight(): void {
    rotation.value = normalizeRotation(rotation.value + ROTATION_STEP);
  }

  function panBy(dx: number, dy: number): void {
    translateX.value += dx;
    translateY.value += dy;
  }

  function reset(): void {
    scale.value = 1;
    rotation.value = 0;
    translateX.value = 0;
    translateY.value = 0;
  }

  return {
    scale,
    rotation,
    translateX,
    translateY,
    scaleLabel,
    transformStyle,
    zoomIn,
    zoomOut,
    rotateLeft,
    rotateRight,
    panBy,
    reset,
  };
}
